import {
  Breadcrumbs,
  BreadcrumbsItem,
} from '../../../../core/components/organism/Breadcrumbs';
import Exercise from '../../../../core/components/templates/Exercise';
import Container from '../../../../core/components/atoms/Container';
import { useDisciplineExerciseHook } from '../../hooks/useExercise';
import ContentModal from '../../components/contentModal';
import Confetti from 'react-confetti';
import { useEffect, useRef, useState } from 'react';
import { useUserStore } from '../../../Auth/stores/authStore';
import useGetAllExercises from '../../hooks/useGetAllExercises';
import { useLocation, useParams } from 'react-router-dom';
import { getDisciplineName } from '../../../../core/utils/discipline';
import { getModuleLabel } from '../../utils/contentHelper';
import { PopUpModal } from '../../../Gamification/components/Playing/PopUpModal';
import { ExerciseAction, IExercise } from '../../../../core/models/exercise';
import { useShouldShowPopUpScore } from '../../../../core/hooks/useShouldShowPopUpScore';
import {
  applyExerciseFiltersFromUrl,
  useExerciseUrlFilters,
} from '../../hooks/useExerciseUrlFilters';

const ExercisePage = () => {
  const { shouldShowPopUpScore } = useShouldShowPopUpScore();
  const [showModalScore, setShowModalScore] = useState<number | false>(false);
  const [action, setAction] = useState<ExerciseAction | undefined>(undefined);
  const { discipline, module, subject, slug } = useParams();
  const { search } = useLocation();
  const { user } = useUserStore();
  const { writeStoresToUrl } = useExerciseUrlFilters();
  const hasHydratedRef = useRef<string>('');

  // Hidrata os filtros a partir da URL antes do primeiro fetch da lista.
  // Mesmo padrão usado em ContentPage: rodar síncrono no render evita que
  // o efeito do filho dispare antes do efeito do pai.
  const hydrationKey = `${module}:${slug}`;
  if (hasHydratedRef.current !== hydrationKey) {
    hasHydratedRef.current = hydrationKey;
    applyExerciseFiltersFromUrl();
  }

  const { mutate, data, isPending, initialPage, startTime } =
    useGetAllExercises();

  
  const {
    exercises,
    isContentModal,
    rightAnswer,
    updateExercise,
    changeExercise,
    setIsContentModal,
  } = useDisciplineExerciseHook();

  useEffect(() => {
    if (module && slug) {
      mutate({ module: module, slug: slug });
    }
  }, [module, slug, search, mutate, user?.id]);

  useEffect(() => {
    if (rightAnswer) {
      const audio = new Audio(
        'https://storage.aprovatotal.com.br/platform-storage-prod/sounds/songCorrectExercise.mp3'
      );
      if (user && user.exercisePlaySound) {
        audio.play();
      }
    }
    if (action === 'verifyAnswer' && shouldShowPopUpScore) {
      setShowModalScore(rightAnswer ? 2 : 1);
    }
    setAction(undefined);
  }, [rightAnswer, action, shouldShowPopUpScore]);

  const handleExerciseChange = (
    exercise: IExercise,
    action?: ExerciseAction,
    startTime?: Date
  ) => {
    setAction(action);
    updateExercise(exercise, action, startTime);
  };

  const currentExercise =
    initialPage !== undefined ? data?.[initialPage] : undefined;
  // A rota traz o slug do assunto; o título vem dos assuntos da questão atual.
  const currentSubject =
    currentExercise?.subjects?.find((s) => s.slug === subject) ??
    currentExercise?.subjects?.[0];

  const breadcrumbItems: BreadcrumbsItem[] = [
    {
      label: discipline ? getDisciplineName(discipline) : '',
      href: `/disciplinas/${discipline}`,
    },
    {
      label: getModuleLabel(module || ''),
      href: `/disciplinas/${discipline}/modulo/${module}/tipo/exercicio`,
    },
    ...(currentSubject?.title
      ? [{ label: currentSubject.title, href: '#' }]
      : []),
    {
      label: currentExercise?.title || '',
      href: '#',
    },
  ];

  return (
    <>
      {rightAnswer && user?.exerciseShowAnimation && (
        <Confetti
          width={window.innerWidth - 20}
          height={document.body.clientHeight}
          numberOfPieces={300}
        />
      )}
      <Container className='pb-0'>
        <Breadcrumbs items={breadcrumbItems} />
      </Container>
      <Exercise
        exercises={exercises}
        showExerciseNavigation={false}
        initialPage={initialPage}
        onPageChange={(page) => changeExercise(page)}
        showContentList={true}
        isLoading={isPending}
        onClickContentList={() => {
          setIsContentModal(true);
        }}
        onExerciseChange={(exercise, action) => {
          handleExerciseChange(exercise, action, startTime);
        }}
      />
      <ContentModal
        isOpen={isContentModal}
        onClose={() => {
          writeStoresToUrl();
          setIsContentModal(false);
        }}
        slug={slug || ''}
        subtitle={'Lista de Exercícios'}
        discipline={discipline || ''}
        module={module || ''}
        subject={subject || ''}
        initialContent={null}
        initialTab={1}
        contentType='exercicio'
      />
      {showModalScore && (
        <PopUpModal
          isOpen={!!showModalScore}
          onClose={() => setShowModalScore(false)}
          score={showModalScore}
        />
      )}
    </>
  );
};

export default ExercisePage;
